/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback } from '@web-stories-wp/react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { __, _x } from '@web-stories-wp/i18n';
import { DropDown, NumericInput } from '@web-stories-wp/design-system';
/**
 * Internal dependencies
 */
import { Row } from '../../../form';
import { SimplePanel } from '../../panel';
import { getCommonValue, inputContainerStyleOverride } from '../../shared';
import { MULTIPLE_DISPLAY_VALUE, MULTIPLE_VALUE } from '../../../../constants';
import {
  BLEND_MODE,
  getBlendModeForDisplay,
} from '../../../../elements/shared/blendModes';

const MIN_MAX = {
  OPACITY: {
    MIN: 0,
    MAX: 100,
  },
};

const ShortRow = styled(Row)`
  width: 128px;
`;

const BlendModeRow = styled(Row)`
  width: 180px;
`;

const StyledDropDown = styled(DropDown)`
  width: 100%;
`;

const BLEND_MODE_OPTIONS = [
  { label: __('Normal', 'web-stories'), value: BLEND_MODE.NORMAL },
  { label: __('Multiply', 'web-stories'), value: BLEND_MODE.MULTIPLY },
  { label: __('Screen', 'web-stories'), value: BLEND_MODE.SCREEN },
  { label: __('Overlay', 'web-stories'), value: BLEND_MODE.OVERLAY },
  { label: __('Darken', 'web-stories'), value: BLEND_MODE.DARKEN },
  { label: __('Lighten', 'web-stories'), value: BLEND_MODE.LIGHTEN },
  { label: __('Color Dodge', 'web-stories'), value: BLEND_MODE.COLOR_DODGE },
  { label: __('Color Burn', 'web-stories'), value: BLEND_MODE.COLOR_BURN },
  { label: __('Linear Light', 'web-stories'), value: BLEND_MODE.LINEAR_LIGHT },
  { label: __('Hard Light', 'web-stories'), value: BLEND_MODE.HARD_LIGHT },
  { label: __('Soft Light', 'web-stories'), value: BLEND_MODE.SOFT_LIGHT },
  { label: __('Difference', 'web-stories'), value: BLEND_MODE.DIFFERENCE },
  { label: __('Exclusion', 'web-stories'), value: BLEND_MODE.EXCLUSION },
];

function defaultOpacity({ opacity }) {
  return typeof opacity !== 'undefined' ? opacity : MIN_MAX.OPACITY.MAX;
}

function defaultBlendMode({ blendMode }) {
  return getBlendModeForDisplay(blendMode);
}

function LayerStylePanel({ selectedElements, pushUpdate }) {
  const options = selectedElements.every(({ type }) =>
    ['image', 'text', 'shape'].includes(type)
  )
    ? BLEND_MODE_OPTIONS
    : BLEND_MODE_OPTIONS.filter(
        ({ value }) => value !== BLEND_MODE.LINEAR_LIGHT
      );
  const opacity = getCommonValue(selectedElements, defaultOpacity);
  const blendMode = getCommonValue(selectedElements, defaultBlendMode);

  const handleChangeOrBlur = useCallback(
    (value) => pushUpdate({ opacity: value ?? 100 }, true /** submit */),
    [pushUpdate]
  );

  const handleBlendModeChange = useCallback(
    (value) =>
      pushUpdate(
        { blendMode: getBlendModeForDisplay(value) },
        true /** submit */
      ),
    [pushUpdate]
  );

  return (
    <SimplePanel name="layerStyle" title={__('Layer', 'web-stories')}>
      <ShortRow>
        <NumericInput
          suffix={__('Opacity', 'web-stories')}
          unit={_x('%', 'Percentage', 'web-stories')}
          value={opacity}
          onChange={(evt, value) => handleChangeOrBlur(value)}
          onBlur={(evt, value) => handleChangeOrBlur(value)}
          min={MIN_MAX.OPACITY.MIN}
          max={MIN_MAX.OPACITY.MAX}
          aria-label={__('Opacity in percent', 'web-stories')}
          placeholder={opacity === MULTIPLE_VALUE ? MULTIPLE_DISPLAY_VALUE : ''}
          isIndeterminate={opacity === MULTIPLE_VALUE}
          containerStyleOverride={inputContainerStyleOverride}
        />
      </ShortRow>
      <BlendModeRow>
        <StyledDropDown
          ariaLabel={__('Blend mode', 'web-stories')}
          dropDownLabel={__('Blend', 'web-stories')}
          options={options}
          placeholder={
            blendMode === MULTIPLE_VALUE
              ? MULTIPLE_DISPLAY_VALUE
              : BLEND_MODE_OPTIONS[0].label
          }
          selectedValue={blendMode === MULTIPLE_VALUE ? '' : blendMode}
          onMenuItemClick={(evt, value) => handleBlendModeChange(value)}
        />
      </BlendModeRow>
    </SimplePanel>
  );
}

LayerStylePanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default LayerStylePanel;
